<template>
  <div>
    <main-layout
      :showPoweredBy="true"
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row  class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <div class="header-wrapper">
              <div class="title-wrapper">
                <h1 class="header-title">
                  <!--<chevron
                    @click="back()"/>-->
                  {{ $t('explorer.parameterSelection') }}
                </h1>
                <h2>{{ $t('explorer.menu.evolution.label') }}</h2>
              </div>
              <div>
                <btn-pill
                  v-has-feature="'Taxonomy'"
                  class="margin-right-l"
                  btn-type="secondary"
                  btn-size="s"
                  @click="goToTaxonomy()">
                  <font-awesome-icon :icon="icon.tags"/>
                  {{ $t('variables.taxonomy') }}
                </btn-pill>
                <btn-pill
                  v-has-feature="'Query'"
                  v-on:click="goToQueries()"
                  btn-size="s"
                  btn-type="secondary">
                  <font-awesome-icon :icon="icon.queries"/>
                  {{ $t('menu.queries') }}
                </btn-pill>
                  <btn-pill
                    v-has-feature="'AggregateVariable'"
                    v-on:click="goToAggregateVariables()"
                    btn-size="s"
                    btn-type="secondary">
                    <font-awesome-icon :icon="icon.aggregateVariables "/>
                    {{ $t('aggregateVariable.addAggregateVariable') }}
                  </btn-pill>
              </div>
            </div>
          </grid-col>
        </grid-row>
        <grid-row  v-has-feature="'Explore'" class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <div class="selection-title">
              {{ $t('explorer.selectionVariables') }}
              <div>
                <btn-pill
                  class="btn-multiple"
                  @click="onClickMultipleSelection()"
                  btn-size="s"
                  btn-type="secondary">
                  {{ $t('explorer.selectionMultipleVariables') }}
                </btn-pill>
              </div>
            </div>
            <transition-group name="fade">
              <div
                class="selection-wrapper"
                v-for="(selectedVariable, index) in selectedVariables"
                :key="index">
                <variable-selector
                  class="selection-left"
                  v-model="selectedVariable.model"
                  :variables="selectedVariable.options"
                  :tagged-variables="getTaggedVariables"
                  v-on:tagsChanged="tagsChanged"
                  :selected-tags="selectedTags"
                  :tree="getReadTagsTree"/>
                <font-awesome-icon
                  :class="isRemoveButtonDisabled() ? 'selection-right-disabled' : 'selection-right'"
                  v-on:click="onClickRemoveVariable(index)"
                  :icon="icon.remove"/>
              </div>
            </transition-group>
            <div
              class="add-wrapper">
              <btn-pill
                :disabled="isAddButtonDisabled()"
                v-on:click="onClickAddVariable()"
                btn-size="s"
                btn-type="secondary">
                <font-awesome-icon :icon="icon.add"/>
                {{ $t('explorer.selectorModal.header') }}
              </btn-pill>
            </div>
            <div class="selection-title">
              {{ $t('explorer.datesSelection') }}
            </div>
            <date-range-selector
              ref="dateRangeSelector"
              v-model="selectedDates"/>
            <div
              class="action-wrapper">
              <btn-pill
                v-on:click="onClickCancel()"
                :btnText="$t('commons.cancel')"
                btn-size="m"
                btn-type="secondary">
              </btn-pill>
              <btn-pill
                class="margin-left-s"
                :disabled="isExploreDisabled()"
                v-on:click="onClickExplore()"
                :btnText="$t('explorer.menu.explore')"
                btn-size="m"
                btn-type="primary">
              </btn-pill>
            </div>
          </grid-col>
        </grid-row>
      </div>
    </main-layout>
    <variable-multiple-selector-modal
      v-if="modalMultipleSelection"
      :variables="getVariablesMinimal"
      :tree="getReadTagsTree"
      v-model="multipleVariables"
      :tagged-variables="getTaggedVariables"
      :selected-tags="selectedTags"
      v-on:selectedTagsChanged="tagsChanged"
      v-on:input="onMultipleVariablesSelected"
      v-on:close="modalMultipleSelection = false"/>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import MainLayout from '../MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import DateRangePicker from '@/components/shared/DateRangePicker';
import BtnPill from '@/components/shared/buttons/BtnPill';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {
  faTimesCircle,
  faPlusCircle
} from '@fortawesome/pro-solid-svg-icons';
import {faTags} from '@fortawesome/pro-regular-svg-icons';
import {
  faCalculatorAlt,
  faFileChartLine
} from '@fortawesome/pro-light-svg-icons';
import DateRangeSelector from '@/components/explorer/DateRangeSelector';
import VariableSelector from '@/components/variable/VariableSelector';
import Chevron from '@/components/shared/Chevron';
import VariableMultipleSelectorModal from '@/components/variable/VariableMultipleSelectorModal';
import variableApi from '@/api/variable';
import variableSelectorUtils from '@/layouts/variableSelectorUtils';

export default {
  components: {
    VariableMultipleSelectorModal,
    VariableSelector,
    MainLayout,
    GridRow,
    GridCol,
    BtnPill,
    DateRangePicker,
    FontAwesomeIcon,
    DateRangeSelector,
    Chevron
  },
  mixins: [
    variableSelectorUtils
  ],
  data () {
    return {
      selectedDates: [],
      selectedVariables: [],
      modalMultipleSelection: false,
      multipleVariables: [],
      icon: {
        add: faPlusCircle,
        remove: faTimesCircle,
        queries: faFileChartLine,
        aggregateVariables: faCalculatorAlt,
        tags: faTags
      },
      variablesMinimal: [],
      taggedVariables: []
    };
  },
  created () {
    this.retrieveReadTagsTree({ context: this });
    variableApi.getMinimalVariables(this).then(data => {
      this.taggedVariables = this.variablesMinimal = data.body.sort((a, b) => {
        return a.label.localeCompare(b.label, undefined, {numeric: true, sensitivity: 'base'});
      });
      this.onRetrieveVariables();
    });
  },
  computed: {
    ...mapGetters([
      'getReadTagsTree'
    ]),
    getBreadcrumb () {
      return [
        // {name: this.$t('explorer.breadcrumbParent'), link: '/explore'},
        {name: this.$t('explorer.parameterSelection'), disabled: true}
      ];
    },
    getVariablesMinimal () {
      return this.variablesMinimal;
    },
    getTaggedVariables () {
      const variablesInUse = this.selectedVariables.filter(selected => selected.model).map(o => o.model.id);
      return this.taggedVariables.filter(taggedVar => !variablesInUse.includes(taggedVar.id));
    }
  },
  methods: {
    ...mapActions([
      'retrieveReadTagsTree'
    ]),
    back () {
      this.$router.push({name: 'Explore', query: this.$route.query});
    },
    goToTaxonomy () {
      this.$router.push({name: 'VariableTagTree', query: {origin: 'ExplorerEvolutionVariables'}});
    },
    onRetrieveVariables () {
      const query = this.$route.query;

      // Variables
      const variableIds = query.variables;
      if (variableIds) {
        const variableArray = variableIds.split(',');
        for (let i = 0; i < variableArray.length; i++) {
          const id = variableArray[i];
          this.selectedVariables.push({
            options: this.getVariablesForNewSelector(),
            model: this.findVariableModelById(id)
          });
        }
      } else {
        this.selectedVariables.push({
          options: this.getVariablesForNewSelector(),
          model: undefined
        });
      }

      // Dates
      const startDate = query.startDate;
      const endDate = query.endDate;
      if (startDate && endDate) {
        this.selectedDates = [new Date(Number(startDate)), new Date(Number(endDate))];
        this.$refs.dateRangeSelector.selectCustomRange();
      }
    },
    onClickAddVariable () {
      this.selectedVariables.push({
        options: this.getVariablesForNewSelector(),
        model: undefined
      });
    },
    onClickRemoveVariable (index) {
      if (!this.isRemoveButtonDisabled()) {
        this.selectedVariables.splice(index, 1);
      }
    },
    onClickExplore () {
      const query = {
        variables: this.getSelection().join(),
        startDate: this.selectedDates[0].getTime(),
        endDate: this.selectedDates[1].getTime()
      };

      const oldQuery = this.$route.query;
      for (let i in oldQuery) {
        if (oldQuery.hasOwnProperty(i) && i.startsWith('opt')) {
          query[i] = oldQuery[i];
        }
      }

      this.$router.push({path: 'explorer-evolution', query: query});
    },
    onClickCancel () {
      this.selectedVariables = [{
        options: this.getVariablesForNewSelector(),
        model: undefined
      }];
    },
    isAddButtonDisabled () {
      let disabled = false;

      // All selected
      let currentNumberOfVariables = this.selectedVariables.length;
      for (let i = 0; i < currentNumberOfVariables; i++) {
        const model = this.selectedVariables[i].model;
        if (model === undefined || model === null) {
          disabled = true;
          break;
        }
      }

      // No more variables
      disabled = disabled || this.getVariablesForNewSelector().length === 0;

      // Max variables
      disabled = disabled || currentNumberOfVariables === MAXIMUM_NUMBER_OF_ALLOWED_VARIABLES;

      return disabled;
    },
    isRemoveButtonDisabled () {
      // At least one variable
      return this.selectedVariables.length === 1;
    },
    isExploreDisabled () {
      let disabled = true;
      if (this.selectedVariables.length > 0) {
        disabled = false;
        for (let i = 0; i < this.selectedVariables.length; i++) {
          const selectedVariable = this.selectedVariables[i];
          if (!selectedVariable.model) {
            disabled = true;
            break;
          }
        }
      }
      return disabled;
    },
    disabledDate (date) {
      return date > new Date();
    },
    findVariableModelById (id) {
      const variables = this.getVariablesMinimal.slice();
      for (let i = 0; i < variables.length; i++) {
        if (Number(id) === variables[i].id) {
          return variables[i];
        }
      }
      return undefined;
    },
    getVariablesForNewSelector () {
      const variables = this.getVariablesMinimal.slice();
      const size = this.selectedVariables.length;
      for (let i = 0; i < size; i++) {
        const selectedVariableModel = this.selectedVariables[i].model;
        if (selectedVariableModel) {
          // Remove previously selected variable
          const id = selectedVariableModel.id;
          let position = 0;
          for (let j = 0; j < variables.length; j++) {
            if (variables[j].id === id) {
              position = j;
              break;
            }
          }
          variables.splice(position, 1);
        }
      }
      return variables;
    },
    getSelection () {
      const selection = [];
      for (let i = 0; i < this.selectedVariables.length; i++) {
        const selectedVariable = this.selectedVariables[i];
        if (selectedVariable.model) {
          selection.push(selectedVariable.model.id);
        }
      }
      return selection;
    },
    goToQueries () {
      this.$router.push({name: 'QueryList', query: {filter: 'explorer-evolution', origin: 'ExplorerEvolutionVariables'}});
    },
    goToAggregateVariables () {
      this.$router.push({name: 'AggregateOperatedVariable', query: {origin: 'ExplorerEvolutionVariables'}});
    },
    onMultipleVariablesSelected (variables) {
      this.selectedVariables = [];

      for (let i = 0; i < variables.length && i < MAXIMUM_NUMBER_OF_ALLOWED_VARIABLES; i++) {
        const id = variables[i];
        this.selectedVariables.push({
          options: this.getVariablesForNewSelector(),
          model: this.findVariableModelById(id)
        });
      }
    },
    onClickMultipleSelection () {
      this.multipleVariables = [];

      for (let i = 0; i < this.selectedVariables.length; i++) {
        const selectedVariable = this.selectedVariables[i];
        if (selectedVariable.model) {
          this.multipleVariables.push(selectedVariable.model.id);
        }
      }
      this.modalMultipleSelection = true;
    }
  }
};

/**
 * Constant that set the maximun number of variables that the user can select.
 * @type {number}
 */
const MAXIMUM_NUMBER_OF_ALLOWED_VARIABLES = 13;

</script>

<style lang="scss" scoped>
  @import "src/stylesheets/variables";

  .header-wrapper {
    display: flex;
    justify-content: space-between;
  }

  .title-wrapper {
    display: flex;
  }

  h1 {
    margin-right: $sp-margin;
    padding-right: $sp-margin;
    border-right: 1px solid $black-trans6;
  }

  h2 {
    line-height: 2.2;
    font-size: 1.2rem;
    font-weight: lighter;
  }

  .selection-title {
    display: flex;
    font-size: 1.2rem;
    margin-bottom: $sp-margin-small;
    text-align: center;
    align-items: center
  }

  .selection-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: $sp-margin-small;
  }

  .selection-left {
    width: 100%;
    margin: 1px;
    margin-right: $sp-margin-small;
  }

  .btn-multiple {
    margin-left: $sp-margin;
  }

  .selection-right {
    font-size: $fs-xl;
    cursor: pointer;
    color: $primary;
  }

  .selection-right-disabled {
    font-size: $fs-xl;
    color: $primary;
  }

  .add-wrapper {
    margin-top: $sp-margin-small;
    margin-bottom: $sp-margin-larger;
  }

  .action-wrapper {
    float: right;
    margin-top: $sp-margin-small;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s
  }

  .fade-enter,
  .fade-leave-to
    /* .fade-leave-active in <2.1.8 */ {
    opacity: 0
  }

</style>



// WEBPACK FOOTER //
// src/layouts/explorer/ExplorerEvolutionVariables.vue